import clamp from "lodash/clamp";
import { useFormContext, useWatch } from "react-hook-form";
import { AgeRangeAvailableBounds, AgeType } from "reedy-data/api";
import { useTranslation } from "react-i18next";
import { TabsContent } from "~/components/ui/tabs";
import { StyledTabs, type StyledTabItem } from "~/components/styled-tabs";
import { Slider } from "~/components/slider";
import { Text } from "~/components/text";
import { AGE_TYPE_BOUNDS, FilterType } from "~/hooks/search/types";
import {
  formatAgeValue,
  getEffectiveAgeBounds,
  isAgeTypeAvailable,
} from "~/pages/books/components/filters/utils";

const AGE_TYPES = [
  AgeType.INTEREST_AGE_MONTHS,
  AgeType.INTEREST_AGE_YEARS,
  AgeType.READING_AGE_YEARS,
] as const;

const AGE_TICK_STEP: Record<AgeType, number> = {
  [AgeType.INTEREST_AGE_MONTHS]: 6,
  [AgeType.INTEREST_AGE_YEARS]: 20,
  [AgeType.READING_AGE_YEARS]: 20,
};

const getTickValues = (min: number, max: number, step: number) => {
  const values: number[] = [];
  for (let value = min; value < max; value += step) {
    values.push(value);
  }
  values.push(max);
  return values;
};

export const AgeRangeFilter = ({
  name,
  availableBounds,
  hasOtherActiveFilters = false,
  boundsLoading = false,
}: {
  name: string;
  availableBounds?: AgeRangeAvailableBounds | null;
  hasOtherActiveFilters?: boolean;
  boundsLoading?: boolean;
}) => {
  const { t } = useTranslation(["books"]);
  const { setValue } = useFormContext();

  // useWatch subscribes this component without an ancestor watching all filters.
  const activeAgeType =
    (useWatch({ name: `filters.${name}.ageType` }) as AgeType | undefined) ??
    AgeType.INTEREST_AGE_YEARS;
  const range =
    useWatch({ name: `filters.${name}.range` }) ??
    AGE_TYPE_BOUNDS[activeAgeType];

  const unavailableLabel = t("books:filters.ageRange.unavailable");
  const tabLabels: Record<AgeType, string> = {
    [AgeType.INTEREST_AGE_MONTHS]: t(
      "books:filters.ageRange.tabs.interestAgeMonths",
    ),
    [AgeType.INTEREST_AGE_YEARS]: t(
      "books:filters.ageRange.tabs.interestAgeYears",
    ),
    [AgeType.READING_AGE_YEARS]: t(
      "books:filters.ageRange.tabs.readingAgeYears",
    ),
  };

  const tabs: StyledTabItem<AgeType>[] = AGE_TYPES.map((ageType) => {
    const isAvailable = isAgeTypeAvailable(ageType, availableBounds);
    return {
      value: ageType,
      label: tabLabels[ageType],
      disabled: !isAvailable,
      title: isAvailable ? undefined : unavailableLabel,
    };
  });

  // Set the whole filter object so null-seeded ranges still carry type for ValidAgeRangeFilterSchema.
  const handleTabChange = (ageType: AgeType) => {
    setValue(`filters.${name}`, {
      type: FilterType.AGE_RANGE,
      ageType,
      range: { ...AGE_TYPE_BOUNDS[ageType] },
    });
  };

  const handleSliderChange = (values: number[]) => {
    // Dragging to the full extent of the (possibly narrowed) available bounds
    // means "no constraint", so store the caps range: that is what
    // ValidAgeRangeFilterSchema treats as inactive, keeping the badge, URL,
    // analytics and query in sync with the "all ages" label.
    const bounds = getEffectiveAgeBounds(
      activeAgeType,
      availableBounds,
      hasOtherActiveFilters,
    );
    const atFullExtent = values[0] === bounds.min && values[1] === bounds.max;
    setValue(`filters.${name}`, {
      type: FilterType.AGE_RANGE,
      ageType: activeAgeType,
      range: atFullExtent
        ? { ...AGE_TYPE_BOUNDS[activeAgeType] }
        : { min: values[0], max: values[1] },
    });
  };

  return (
    <StyledTabs<AgeType>
      variant="underline"
      activeTab={activeAgeType}
      onTabChange={handleTabChange}
      tabs={tabs}
      listClassName="h-auto min-h-12 w-[calc(100%+1rem)] -translate-x-1 -translate-y-1 items-stretch justify-start bg-secondary-25 border-b border-secondary-100"
      triggerClassName="h-auto whitespace-normal text-center leading-tight py-2"
    >
      {AGE_TYPES.map((ageType) => {
        const bounds = getEffectiveAgeBounds(
          ageType,
          availableBounds,
          hasOtherActiveFilters,
        );
        // A collapsed span means one available age; render it fixed instead of
        // handing Radix a zero-width range.
        const isCollapsed = bounds.min === bounds.max;
        const displayRange = {
          min: clamp(range.min, bounds.min, bounds.max),
          max: clamp(range.max, bounds.min, bounds.max),
        };
        const isFullRange =
          displayRange.min === bounds.min && displayRange.max === bounds.max;
        const tickValues = getTickValues(
          bounds.min,
          bounds.max,
          AGE_TICK_STEP[ageType],
        );

        const getRangeLabel = () => {
          if (isCollapsed) return formatAgeValue(ageType, bounds.min, t);
          if (isFullRange) return t("books:filters.ageRange.allAges");
          return `${formatAgeValue(ageType, displayRange.min, t)} - ${formatAgeValue(ageType, displayRange.max, t)}`;
        };

        if (!isAgeTypeAvailable(ageType, availableBounds)) {
          return (
            <TabsContent key={ageType} value={ageType}>
              <div className="p-2">
                <Text as="span" variant="muted">
                  {unavailableLabel}
                </Text>
              </div>
            </TabsContent>
          );
        }

        return (
          <TabsContent key={ageType} value={ageType}>
            <div className="flex flex-col gap-4 p-2">
              <div className="flex items-center justify-between">
                <Text as="span" variant="bodyStrong">
                  {getRangeLabel()}
                </Text>
                <Text as="span" variant="muted">
                  {ageType === AgeType.INTEREST_AGE_MONTHS
                    ? t("books:filters.ageRange.axisMonths")
                    : t("books:filters.ageRange.axisYears")}
                </Text>
              </div>
              {!isCollapsed && (
                <>
                  <Slider
                    min={bounds.min}
                    max={bounds.max}
                    step={1}
                    value={[displayRange.min, displayRange.max]}
                    onValueChange={handleSliderChange}
                    disabled={boundsLoading}
                    thumbLabels={[
                      t("books:filters.ageRange.minAge"),
                      t("books:filters.ageRange.maxAge"),
                    ]}
                  />
                  <div className="flex justify-between">
                    {tickValues.map((tick) => (
                      <Text key={tick} as="span" variant="caption">
                        {tick}
                      </Text>
                    ))}
                  </div>
                </>
              )}
            </div>
          </TabsContent>
        );
      })}
    </StyledTabs>
  );
};
